import { useState, useRef, useEffect } from 'react';
import type { SessionInfo } from '../types/gateway';
import styles from './SessionMenu.module.css';

interface SessionMenuProps {
  sessions: SessionInfo[];
  activeSessionId: string | null;
  onCreateSession: () => void;
  onSwitchSession: (id: string) => void;
}

export function SessionMenu({
  sessions,
  activeSessionId,
  onCreateSession,
  onSwitchSession,
}: SessionMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  function handleNewChat() {
    onCreateSession();
    setOpen(false);
  }

  function handleSwitch(id: string) {
    onSwitchSession(id);
    setOpen(false);
  }

  function formatTime(dateStr: string): string {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMin = Math.floor(diffMs / 60_000);

      if (diffMin < 1) return 'just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.floor(diffHr / 24);
      return `${diffDay}d ago`;
    } catch {
      return '';
    }
  }

  return (
    <div className={styles.sessionMenu} ref={menuRef}>
      <button className={styles.newChatBtn} onClick={handleNewChat} title="New Chat">
        <span className={styles.newChatIcon}>+</span>
        <span>New Chat</span>
      </button>

      {sessions.length > 0 && (
        <>
          <button
            className={styles.dropdownToggle}
            onClick={() => setOpen((prev) => !prev)}
            title="Session list"
            aria-expanded={open}
          >
            {open ? '\u25B4' : '\u25BE'}
          </button>

          {open && (
            <div className={styles.dropdown}>
              <div className={styles.dropdownHeader}>Sessions</div>
              {sessions.map((session) => (
                <button
                  key={session.id}
                  className={`${styles.sessionItem} ${session.id === activeSessionId ? styles.sessionItemActive : ''}`}
                  onClick={() => handleSwitch(session.id)}
                >
                  <span className={styles.sessionName}>
                    {session.name || `Session ${session.id.slice(0, 8)}`}
                  </span>
                  <span className={styles.sessionMeta}>
                    {formatTime(session.lastMessageAt || session.createdAt)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
